import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Users, 
  Share2, 
  Gift, 
  Copy, 
  Star, 
  Crown,
  TrendingUp,
  Award,
  Link,
  QrCode,
  Mail,
  MessageSquare,
  Twitter,
  Check,
  Trophy
} from 'lucide-react';
import { toast } from 'sonner';

interface ReferralStats {
  totalReferrals: number;
  activeReferrals: number;
  totalEarnings: number;
  monthlyEarnings: number;
  tier: 'bronze' | 'silver' | 'gold' | 'diamond';
  nextTierRequirement: number;
}

interface Referral {
  id: string;
  username: string;
  joinDate: Date;
  status: 'active' | 'inactive';
  totalVolume: number;
  earnings: number;
  tier: string;
}

interface Reward {
  id: string;
  title: string;
  description: string;
  progress: number;
  total: number;
  reward: string;
  completed: boolean;
}

export function ReferralProgram() {
  const [referralCode] = useState('NDH-CRYPTO-MASTER-2024');
  const [copied, setCopied] = useState(false);

  const stats: ReferralStats = {
    totalReferrals: 47,
    activeReferrals: 32,
    totalEarnings: 1247.50,
    monthlyEarnings: 89.30,
    tier: 'gold',
    nextTierRequirement: 75
  };

  const referrals: Referral[] = [
    {
      id: '1',
      username: 'CryptoNewbie_01',
      joinDate: new Date('2024-01-15'),
      status: 'active',
      totalVolume: 15420,
      earnings: 154.20,
      tier: 'silver'
    },
    {
      id: '2',
      username: 'TraderPro_99',
      joinDate: new Date('2024-02-03'),
      status: 'active',
      totalVolume: 28350,
      earnings: 283.50,
      tier: 'gold'
    },
    {
      id: '3',
      username: 'DeFi_Explorer',
      joinDate: new Date('2024-02-20'),
      status: 'inactive',
      totalVolume: 7830,
      earnings: 78.30,
      tier: 'bronze'
    }
  ];

  const rewards: Reward[] = [
    {
      id: '1',
      title: 'Social Butterfly',
      description: 'Refer 10 new users',
      progress: 8,
      total: 10,
      reward: '500 NDH + Exclusive NFT',
      completed: false
    },
    {
      id: '2',
      title: 'Volume Master',
      description: 'Generate $100K in referral trading volume',
      progress: 87500,
      total: 100000,
      reward: '1000 NDH + Diamond Tier',
      completed: false
    },
    {
      id: '3',
      title: 'Influencer',
      description: 'Have 25 active referrals',
      progress: 32,
      total: 25,
      reward: 'Custom Referral Link',
      completed: true
    }
  ];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'diamond': return 'from-cyan-400 to-blue-600';
      case 'gold': return 'from-yellow-400 to-orange-500';
      case 'silver': return 'from-gray-300 to-gray-500';
      case 'bronze': return 'from-orange-400 to-red-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getTierBenefits = (tier: string) => {
    switch (tier) {
      case 'diamond': return { commission: 15, bonus: 'Priority support + Custom features' };
      case 'gold': return { commission: 12, bonus: 'Advanced analytics' };
      case 'silver': return { commission: 8, bonus: 'Extended limits' };
      case 'bronze': return { commission: 5, bonus: 'Basic rewards' };
      default: return { commission: 3, bonus: 'Standard features' };
    }
  };

  const currentBenefits = getTierBenefits(stats.tier);
  const referralLink = `https://ndhwallet.app/ref/${referralCode}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2">
            <Users className="h-6 w-6" />
            Referral Program
          </h2>
          <p className="text-muted-foreground">
            Earn NDH by inviting friends to join the NDH ecosystem
          </p>
        </div>
        <Badge className={`bg-gradient-to-r ${getTierColor(stats.tier)} text-white`}>
          {stats.tier.toUpperCase()} TIER
        </Badge>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Referrals</p>
                <p className="text-2xl font-bold">{stats.totalReferrals}</p>
              </div>
              <Users className="h-8 w-8 opacity-90" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Active This Month</p>
                <p className="text-2xl font-bold">{stats.activeReferrals}</p>
              </div>
              <TrendingUp className="h-8 w-8 opacity-90" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Earnings</p>
                <p className="text-2xl font-bold">${stats.totalEarnings}</p>
              </div>
              <Gift className="h-8 w-8 opacity-90" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">This Month</p>
                <p className="text-2xl font-bold">${stats.monthlyEarnings}</p>
              </div>
              <Star className="h-8 w-8 opacity-90" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tier Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            Tier Progress
          </CardTitle>
          <CardDescription>
            Refer more users to unlock higher tiers and better rewards
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span>Current: {stats.tier.toUpperCase()} Tier</span>
            <span>{currentBenefits.commission}% Commission Rate</span>
          </div>
          <Progress value={(stats.totalReferrals / stats.nextTierRequirement) * 100} className="h-3" />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{stats.totalReferrals} referrals</span>
            <span>{stats.nextTierRequirement} needed for Diamond</span>
          </div>
          <div className="text-sm font-medium text-purple-600">
            Benefits: {currentBenefits.bonus}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="share" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="share">Share & Earn</TabsTrigger>
          <TabsTrigger value="referrals">My Referrals</TabsTrigger>
          <TabsTrigger value="rewards">Reward Challenges</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
        </TabsList>

        <TabsContent value="share" className="space-y-6">
          {/* Referral Link */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link className="h-5 w-5" />
                Your Referral Link
              </CardTitle>
              <CardDescription>
                Share this link to earn {currentBenefits.commission}% commission on all trades
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input 
                  value={referralLink}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button 
                  onClick={() => copyToClipboard(referralLink)}
                  className="flex items-center gap-2"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <QrCode className="h-4 w-4" />
                  QR Code
                </Button>
                <Button variant="outline" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </Button>
                <Button variant="outline" className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  WhatsApp
                </Button>
                <Button variant="outline" className="flex items-center gap-2">
                  <Twitter className="h-4 w-4" />
                  Twitter
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Referral Code */}
          <Card>
            <CardHeader>
              <CardTitle>Referral Code</CardTitle>
              <CardDescription>
                Users can also enter this code during signup
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input 
                  value={referralCode}
                  readOnly
                  className="font-mono text-lg font-bold text-center"
                />
                <Button 
                  onClick={() => copyToClipboard(referralCode)}
                  variant="outline"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Commission Structure */}
          <Card>
            <CardHeader>
              <CardTitle>Commission Structure</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="font-medium">Trading Commissions</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Spot Trading</span>
                      <span className="font-medium">{currentBenefits.commission}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Futures Trading</span>
                      <span className="font-medium">{currentBenefits.commission + 2}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Staking Rewards</span>
                      <span className="font-medium">{Math.floor(currentBenefits.commission / 2)}%</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="font-medium">Bonus Rewards</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>First Trade Bonus</span>
                      <span className="font-medium">50 NDH</span>
                    </div>
                    <div className="flex justify-between">
                      <span>KYC Completion</span>
                      <span className="font-medium">25 NDH</span>
                    </div>
                    <div className="flex justify-between">
                      <span>First Stake</span>
                      <span className="font-medium">100 NDH</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referrals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>My Referrals ({referrals.length})</CardTitle>
              <CardDescription>
                Track your referred users and their activity
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {referrals.map((referral) => (
                  <div key={referral.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                        {referral.username.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{referral.username}</p>
                        <p className="text-sm text-muted-foreground">
                          Joined {referral.joinDate.toLocaleDateString()}
                        </p>
                      </div>
                      <Badge className={`${getTierColor(referral.tier)} text-white`}>
                        {referral.tier}
                      </Badge>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${referral.earnings}</p>
                      <p className="text-sm text-muted-foreground">
                        Vol: ${referral.totalVolume.toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rewards" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5" />
                Reward Challenges
              </CardTitle>
              <CardDescription>
                Complete challenges to earn extra NDH and exclusive rewards
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {rewards.map((reward) => (
                <div key={reward.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{reward.title}</h4>
                      <p className="text-sm text-muted-foreground">{reward.description}</p>
                    </div>
                    {reward.completed && (
                      <Badge variant="default" className="bg-green-500">
                        <Award className="h-3 w-3 mr-1" />
                        Completed
                      </Badge>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress: {reward.progress.toLocaleString()}/{reward.total.toLocaleString()}</span>
                      <span className="font-medium text-purple-600">{reward.reward}</span>
                    </div>
                    <Progress value={(reward.progress / reward.total) * 100} className="h-2" />
                  </div>

                  {reward.completed && (
                    <Button size="sm" className="w-full">
                      <Gift className="h-4 w-4 mr-2" />
                      Claim Reward
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaderboard" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Top Referrers This Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((rank) => (
                  <div key={rank} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : rank === 3 ? 'bg-orange-500' : 'bg-gray-200'
                      } text-white font-bold text-sm`}>
                        {rank}
                      </div>
                      <div>
                        <p className="font-medium">Referrer_{rank}</p>
                        <p className="text-sm text-muted-foreground">
                          {65 - rank * 10} referrals • Diamond tier
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">${(2500 - rank * 300).toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">This month</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}